import { z } from "zod";
import { FaceitMatchId } from "./faceit.ts";

/**
 * Demos are kept as matches: a match (a BO1, BO3 or BO5) holds its maps in the order
 * they were played, each map one demo. A match lives in a team's library or in the
 * shared Pro matches one. The demo file stays on the uploader's PC; what is kept is
 * each map's replay (see replay.ts).
 */
export const DEMO_SOURCES = ["faceit", "premier", "pro", "scrim", "other"] as const;
export const DemoSource = z.enum(DEMO_SOURCES);
export type DemoSource = z.infer<typeof DemoSource>;

/** Where matches live: a team (its id) or the shared Pro matches library. */
export const DemoScope = z.union([z.literal("pro"), z.string().uuid()]);
export type DemoScope = z.infer<typeof DemoScope>;

/** A gzipped replay is at most this big (a long overtime match is about 3 MB). */
export const MAX_REPLAY_BYTES = 20 * 1024 * 1024;
/** Unpacked, at most this big. */
export const MAX_REPLAY_UNPACKED_BYTES = 200 * 1024 * 1024;
/** Maps (demos) a library keeps. */
export const MAX_DEMOS_PER_LIBRARY = 500;
/** Best of 1, 3 or 5. */
/** BO2 is played to a draw at 1–1, with no decider. */
export const BEST_OF = [1, 2, 3, 5] as const;
export type BestOf = (typeof BEST_OF)[number];
export const MAX_MAPS_PER_MATCH = 5;

/**
 * Who of the team played, from the demos' players and the roster:
 * team = 3 or more of us on one side, stack = 2, solo = 1, scouting = none of us
 * (another team's match); pro = the shared Pro matches library.
 */
export const MATCH_LABELS = ["team", "stack", "solo", "scouting", "pro"] as const;
export type MatchLabel = (typeof MATCH_LABELS)[number];

export const MatchMetaInput = z.object({
  title: z.string().trim().min(1, "Give the match a title.").max(120),
  source: DemoSource,
  /** Tournament or league. */
  event: z.string().trim().max(120).nullable().default(null),
  /** Group, round, playoffs… */
  stage: z.string().trim().max(80).nullable().default(null),
  bestOf: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(5)]).default(1),
  /** When it was played (ISO), when known. */
  playedAt: z.string().datetime({ offset: true }).nullable().default(null),
  /** The FACEIT match it is, when known: marks it imported in the FACEIT list. */
  faceitMatchId: FaceitMatchId.nullable().optional(),
});
export type MatchMetaInput = z.infer<typeof MatchMetaInput>;

export const CreateMatchInput = MatchMetaInput.extend({ scope: DemoScope });
export type CreateMatchInput = z.infer<typeof CreateMatchInput>;

export const UpdateMatchInput = MatchMetaInput;
export type UpdateMatchInput = z.infer<typeof UpdateMatchInput>;

/** A map's replay is sent with: the match it goes into, and the demo file it came from. */
export const DemoUploadInput = z.object({
  matchId: z.string().uuid(),
  /** The demo's file name, for Open in CS2. */
  fileName: z.string().trim().min(1).max(200),
  /** SHA-256 (hex) of the demo's size and its first and last 8 MB. */
  fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
});
export type DemoUploadInput = z.infer<typeof DemoUploadInput>;

/** Moves maps of another match into this one (after its own). */
export const MergeMatchInput = z.object({ from: z.string().uuid() });
export type MergeMatchInput = z.infer<typeof MergeMatchInput>;

export interface MemberRef {
  id: string;
  name: string;
  avatarUrl: string | null;
}

/** A map of a match, its score read from the match's first side (A). */
export interface MatchMap {
  demoId: string;
  number: number;
  map: string;
  score: [number, number];
  rounds: number;
  durationSeconds: number;
  /** Which side won the map, null for a draw. */
  winner: "a" | "b" | null;
  /**
   * The demos of the map after the first, when it was relaunched (a crash, a reset) and
   * went on in another demo: they count as one map, `demoId` being the first.
   */
  parts: string[];
  /** The map's first demo starts from a restored score: it may go on from the map before. */
  restored: boolean;
}

export interface MatchSummary {
  id: string;
  teamId: string | null;
  pro: boolean;
  title: string;
  source: DemoSource;
  event: string | null;
  stage: string | null;
  bestOf: BestOf;
  playedAt: string | null;
  label: MatchLabel;
  /** Side A is always ours when we played; for scouting and pro, the first map's first team. */
  sides: [string, string];
  /** Maps won by A and by B. */
  result: [number, number];
  /** From our side: only when we played (team, stack, solo). */
  outcome: "win" | "loss" | "draw" | null;
  maps: MatchMap[];
  /** Team members who played, on our side. */
  members: MemberRef[];
  /** Players on our side who are not in the team (pugs). */
  randoms: number;
  uploadedBy: { id: string; name: string } | null;
  /** The FACEIT match it is, when known. */
  faceitMatchId: string | null;
  canEdit: boolean;
  createdAt: string;
}

export interface MatchPlayer {
  steamId: string;
  name: string;
  side: "a" | "b";
  member: MemberRef | null;
  maps: number;
  rounds: number;
  kills: number;
  deaths: number;
  assists: number;
  damage: number;
  adr: number;
}

export interface MatchDetail extends MatchSummary {
  players: MatchPlayer[];
}

export interface DemoPlayer {
  steamId: string;
  name: string;
  team: 0 | 1;
  /** The team member with this Steam ID, when the match is a team's. */
  member: MemberRef | null;
}

/** One map, as the viewer opens it. */
export interface DemoDetail {
  id: string;
  map: string;
  number: number;
  fileName: string;
  replayUrl: string;
  replayBytes: number;
  writer: string;
  format: number;
  players: DemoPlayer[];
  /** Which of the demo's teams (0 or 1) is the match's side A. */
  aTeam: 0 | 1;
  match: MatchSummary;
  /** Grenades the team said are not a stuff: [grenade index, stuff id]. */
  dismissed: [number, string][];
  /** The map's demos in the order played, this one first: more than one when it was relaunched. */
  parts: DemoPart[];
  /** Whether I may add voice tracks: the team's players; admins on a Pro match. */
  canAddVoice: boolean;
}

/** One demo of a map played over several. */
export interface DemoPart {
  id: string;
  fileName: string;
  replayUrl: string;
  replayBytes: number;
  format: number;
  /** Rounds already played when it starts. */
  startScore: number;
}

/** "This grenade is not that stuff." */
export const DismissInput = z.object({ grenade: z.number().int().min(0).max(5000), stuffId: z.string().uuid() });
export type DismissInput = z.infer<typeof DismissInput>;

/** Answer to "is this demo already there?", asked before parsing it. */
export interface DemoCheck {
  existing: { id: string; matchId: string; title: string } | null;
}

// ---------------------------------------------------------------- lineups

export interface LineupMap {
  players: { steamId: string; team: 0 | 1 }[];
  score: [number, number];
}

export interface Lineup {
  label: Exclude<MatchLabel, "pro">;
  /** Per map: which of its teams is side A. */
  aTeams: (0 | 1)[];
  /** Most of us on one side in any map. */
  ours: number;
  /** Side A is ours. */
  weArePresent: boolean;
}

const labelFor = (n: number): Lineup["label"] => (n >= 3 ? "team" : n === 2 ? "stack" : n === 1 ? "solo" : "scouting");

/**
 * Who of the roster played and on which side, map by map. Side A is ours when we
 * played; otherwise the first map's first team, and later maps follow it by the
 * players they share (team numbers in a demo say nothing across maps).
 */
export function lineupOf(maps: LineupMap[], roster: ReadonlySet<string>): Lineup {
  let ours = 0;
  const ourTeam = maps.map((m) => {
    const count = [0, 0];
    for (const p of m.players) if (roster.has(p.steamId)) count[p.team]++;
    ours = Math.max(ours, count[0], count[1]);
    if (count[0] === 0 && count[1] === 0) return null;
    return count[1] > count[0] ? 1 : 0;
  });
  const first = maps[0];
  const reference = new Set(first ? first.players.filter((p) => p.team === (ourTeam[0] ?? 0)).map((p) => p.steamId) : []);
  const aTeams = maps.map((m, i): 0 | 1 => {
    const mine = ourTeam[i];
    if (mine !== null) return mine as 0 | 1;
    if (i === 0) return 0;
    const shared = [0, 0];
    for (const p of m.players) if (reference.has(p.steamId)) shared[p.team]++;
    return shared[1] > shared[0] ? 1 : 0;
  });
  return { label: labelFor(ours), aTeams, ours, weArePresent: ours > 0 };
}

/** A map's score from side A, and who won it. */
export function mapFromA(score: [number, number], aTeam: 0 | 1): { score: [number, number]; winner: "a" | "b" | null } {
  const s: [number, number] = aTeam === 0 ? [score[0], score[1]] : [score[1], score[0]];
  return { score: s, winner: s[0] > s[1] ? "a" : s[1] > s[0] ? "b" : null };
}

/** Maps won by each side, and the outcome from ours when we played. */
export function matchResult(maps: { winner: "a" | "b" | null }[], weArePresent: boolean): { result: [number, number]; outcome: MatchSummary["outcome"] } {
  const result: [number, number] = [maps.filter((m) => m.winner === "a").length, maps.filter((m) => m.winner === "b").length];
  if (!weArePresent || maps.length === 0) return { result, outcome: null };
  return { result, outcome: result[0] > result[1] ? "win" : result[1] > result[0] ? "loss" : "draw" };
}

/** Maps in one file drop that belong to one match: the same players, on the same day. */
export function sameSeries(a: { players: { steamId: string }[]; playedAt: number }, b: { players: { steamId: string }[]; playedAt: number }): boolean {
  const ids = new Set(a.players.map((p) => p.steamId));
  const shared = b.players.filter((p) => ids.has(p.steamId)).length;
  const needed = Math.max(1, Math.ceil(Math.min(a.players.length, b.players.length) * 0.6));
  return shared >= needed && Math.abs(a.playedAt - b.playedAt) <= 18 * 3600_000;
}

// ---------------------------------------------------------------- notes at a moment

export const MAX_COMMENTS_PER_DEMO = 300;

const Coord = z.number().min(-20_000).max(20_000);
const PinLevel = z.enum(["upper", "lower"]).optional();

/**
 * What a note points at on the map (world units): a spot, a spot and where to look
 * from it, a player (the pin follows them), or a small area.
 */
export const NotePin = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("spot"), x: Coord, y: Coord, level: PinLevel }),
  z.object({ kind: z.literal("view"), x: Coord, y: Coord, tx: Coord, ty: Coord, level: PinLevel }),
  /** A player of the replay, by index (a replay holds 24 at most). */
  /** `name`: the player's name when the note was written, for lists read without the replay. */
  z.object({ kind: z.literal("player"), player: z.number().int().min(0).max(23), name: z.string().max(64).optional() }),
  z.object({ kind: z.literal("area"), x: Coord, y: Coord, r: z.number().min(32).max(2000), level: PinLevel }),
]);
export type NotePin = z.infer<typeof NotePin>;
export const NOTE_PIN_KINDS = ["spot", "view", "player", "area"] as const;
export type NotePinKind = (typeof NOTE_PIN_KINDS)[number];

/** The side a note is about. */
export const NoteSide = z.enum(["t", "ct"]);
export type NoteSide = z.infer<typeof NoteSide>;

export const MAX_NOTE_LINKS = 5;
/**
 * Another match a note points to ("they always play B like this, see …"): the match, and
 * optionally one of its maps (its first demo) and a round of it (0 for the first).
 */
export const NoteLinkInput = z.object({
  matchId: z.string().uuid(),
  demoId: z.string().uuid().nullable().default(null),
  round: z.number().int().min(0).max(100).nullable().default(null),
});
export type NoteLinkInput = z.input<typeof NoteLinkInput>;

/** A link as shown: what it opens, or `missing` when that match or map was deleted since. */
export interface NoteLink {
  matchId: string;
  demoId: string | null;
  round: number | null;
  missing: boolean;
  matchTitle: string | null;
  map: string | null;
  mapNumber: number | null;
}

/** The team a note is about: one of the demo's two (0 or 1, as in the replay), and its name in the match. */
export interface NoteTeam {
  index: 0 | 1;
  name: string;
}

export const CommentInput = z.object({
  /** 0 for the first round. */
  round: z.number().int().min(0).max(100),
  tick: z.number().int().min(0),
  text: z.string().trim().min(1, "Write the note.").max(500),
  /** A strat board made from this moment. */
  stratId: z.string().uuid().nullable().default(null),
  pin: NotePin.nullable().default(null),
  /** Seconds since the round's freeze time began: where the note is, read without the replay. */
  seconds: z.number().min(0).max(600).nullable().default(null),
  side: NoteSide.nullable().default(null),
  /** The demo's team the note is about (0 or 1, as in the replay). */
  team: z
    .union([z.literal(0), z.literal(1)])
    .nullable()
    .default(null),
  links: z.array(NoteLinkInput).max(MAX_NOTE_LINKS).default([]),
});
export type CommentInput = z.input<typeof CommentInput>;

/** A note changed by its author: what is sent changes, the moment stays. */
export const CommentUpdate = z
  .object({
    text: z.string().trim().min(1, "Write the note.").max(500),
    pin: NotePin.nullable(),
    side: NoteSide.nullable(),
    team: z.union([z.literal(0), z.literal(1)]).nullable(),
    links: z.array(NoteLinkInput).max(MAX_NOTE_LINKS),
  })
  .partial()
  .refine((u) => Object.keys(u).length > 0, "Nothing to change.");
export type CommentUpdate = z.input<typeof CommentUpdate>;

export interface DemoComment {
  id: string;
  round: number;
  tick: number;
  text: string;
  stratId: string | null;
  pin: NotePin | null;
  /** Seconds into the round, for notes written from 0.18.0. */
  seconds: number | null;
  side: NoteSide | null;
  team: NoteTeam | null;
  links: NoteLink[];
  author: MemberRef | null;
  /** Only its author edits a note. */
  canEdit: boolean;
  canDelete: boolean;
  createdAt: string;
  editedAt: string | null;
}

/** A note of a match, on the match page: which map, which round, where to open it. */
export interface MatchNote extends Omit<DemoComment, "canDelete" | "canEdit"> {
  demoId: string;
  matchId: string;
  matchTitle: string;
  /** When the match was played (else imported), for the library's list. */
  playedAt: string;
  /** The map's number in the match (1, 2, 3). */
  mapNumber: number;
  map: string;
  /** Player names for a note pointing at a player. */
  playerName: string | null;
}

/** A gzipped map mesh for the 3D viewer is a few MB; this leaves room for bigger maps. */
export const MAX_MAP_MESH_BYTES = 8 * 1024 * 1024;
/** The textured map (glTF with its pictures and light map): about 16 MB a map. */
export const MAX_TEXTURED_MAP_BYTES = 64 * 1024 * 1024;

/** A map's 3D mesh, as uploaded by an admin (tools/map-export). */
export interface MapMeshInfo {
  map: string;
  id: string;
  /** Where the file is: it never changes, a new upload gets a new address. */
  url: string;
  bytes: number;
  triangles: number;
  /** When the game's map file was last changed, as the export saw it. */
  source: string;
  uploadedAt: string;
  uploadedBy: string | null;
  /** The map as it looks, when uploaded: drawn instead of the plain mesh. */
  textured: TexturedMapInfo | null;
}

export interface TexturedMapInfo {
  id: string;
  url: string;
  bytes: number;
  triangles: number;
  source: string;
  uploadedAt: string;
}

/** Whether a second list of players has the teams the other way round (0 ↔ 1), by who is in them. */
export function teamsSwapped(a: readonly { steamId: string; team: number }[], b: readonly { steamId: string; team: number }[]): boolean {
  const teamOf = new Map(a.map((p) => [p.steamId, p.team]));
  const votes = [0, 0];
  for (const p of b) {
    const t = teamOf.get(p.steamId);
    if (t !== undefined) votes[t === p.team ? 0 : 1]++;
  }
  return votes[1] > votes[0];
}

/** A demo as folding reads it. */
export interface FoldDemo {
  id: string;
  continuesId: string | null;
}

/**
 * The maps of a match from its demos (in their order): a demo that goes on from another
 * (a relaunched map) is folded after it, the chain being one map. A demo whose demo
 * before is gone, or that a second demo also claims to go on from, stands on its own.
 */
export function foldParts<T extends FoldDemo>(rows: readonly T[]): T[][] {
  const ids = new Set(rows.map((d) => d.id));
  const next = new Map<string, T>();
  for (const d of rows) if (d.continuesId && d.continuesId !== d.id && ids.has(d.continuesId) && !next.has(d.continuesId)) next.set(d.continuesId, d);
  const parts = new Set([...next.values()].map((d) => d.id));
  const seen = new Set<string>();
  const out: T[][] = [];
  const chainFrom = (d: T) => {
    const chain: T[] = [];
    for (let n: T | undefined = d; n && !seen.has(n.id); n = next.get(n.id)) {
      seen.add(n.id);
      chain.push(n);
    }
    return chain;
  };
  for (const d of rows) if (!parts.has(d.id)) out.push(chainFrom(d));
  // A loop has no first demo: each of its demos stands on its own.
  for (const d of rows)
    if (!seen.has(d.id)) {
      out.push([d]);
      seen.add(d.id);
    }
  return out;
}
